import React, { useCallback } from 'react'
import useRoutes from '../../state/useRoutes'
import styles from './CreateLearningPathPage.module.scss'
import { useTranslation } from 'react-i18next'
import LearningPathForm, { LearningPathFormValues } from '../../components/LearningPathForm/LearningPathForm'
import { FormikErrors } from 'formik/dist/types'
import setFormErrors from '../../utilities/setFormErrors'
import useCreateLearningPath, { CreateLearningPathArgs } from '../../state/mutations/useCreateLearningPath'
import { LearningItemStatus } from '../../models/BaseModels'

export function CreateLearningPathPageBreadcrumb() {
  const { t } = useTranslation()

  return <span>{t('edit learning.create learning path')}</span>
}

export default React.memo(function CreateLearningPathPage() {
  const { t } = useTranslation()
  const routes = useRoutes()
  const handleClose = useCallback(() => routes.courses.goto({}), [routes])
  const createLearningPath = useCreateLearningPath()

  async function submitCreation(
    values: LearningPathFormValues,
    setStatus: (status?: any) => void,
    setErrors: (errors: FormikErrors<LearningPathFormValues>) => void
  ) {
    let createdLearningItem: { uuid: string } | null = null

    try {
      setStatus()

      const dataToCreate = prepareDataForCreate(values)

      createdLearningItem = await createLearningPath.mutateAsync(dataToCreate).catch((error) => {
        setFormErrors({ setStatus, setErrors, t }, error)
      })

      routes.course.goto({ uuid: createdLearningItem!.uuid })
    } catch (error) {
      setFormErrors({ setStatus, setErrors, t }, error)
    }
  }

  return (
    <article className={styles.CreateLearningPathPage}>
      <LearningPathForm
        initialValues={initialValues()}
        onSubmit={(data, setStatus, setErrors) => submitCreation(data, setStatus, setErrors)}
        onClose={handleClose}
      />
    </article>
  )
})

function initialValues(): LearningPathFormValues {
  return {
    status: LearningItemStatus.Draft,
    title: '',
    description: '',
    categories: null,
    image: null,
    certificateMode: 'none',
    certificateValidity: null,
    courses: [],
    isAnyoneAssigned: false,
  }
}

function prepareDataForCreate(values: LearningPathFormValues): CreateLearningPathArgs {
  return {
    status: values.status,
    title: values.title,
    description: values.description,
    categories: values.categories?.map(({ uuid }) => uuid) || undefined,
    createCertificate: values.certificateMode !== 'none',
    certificateValidUntil: values.certificateMode === 'limited' ? values.certificateValidity : null,
    image: values.image?.replace(/^[^,]+,/, '') || null,
    courses: values.courses?.map(({ uuid }) => uuid) || undefined,
  }
}
